import { useState, useEffect, useRef } from 'react';
import { CheckCircle, XCircle, Loader2, Mail, Send } from 'lucide-react';
import { API_URL } from '../../lib/env';

interface EmailVerificationProps {
  token: string;
  onComplete: () => void;
}

type VerificationStatus = 'loading' | 'success' | 'error' | 'expired';

export function EmailVerification({ token, onComplete }: EmailVerificationProps) {
  const [status, setStatus] = useState<VerificationStatus>('loading');
  const [message, setMessage] = useState('');
  const [email, setEmail] = useState('');
  const [resendStatus, setResendStatus] = useState<'idle' | 'sending' | 'sent' | 'error'>('idle');
  const [resendMessage, setResendMessage] = useState('');
  const hasVerified = useRef(false);

  useEffect(() => {
    // Prevent double verification (React StrictMode)
    if (hasVerified.current) return;
    hasVerified.current = true;

    const verifyEmail = async () => {
      try {
        const response = await fetch(
          `${API_URL}/console/auth/verify-email?token=${token}`
        );

        const data = await response.json();

        if (response.ok) {
          setStatus('success');
          setMessage('Your email has been verified successfully!');
        } else if (response.status === 400 && (data.detail?.includes('expired') || data.detail?.includes('Invalid'))) {
          setStatus('expired');
          setMessage('Your verification link has expired, but no worries — we can send you a new one.');
        } else {
          setStatus('error');
          setMessage(data.detail || 'Verification failed. Please try again.');
        }
      } catch (error) {
        setStatus('error');
        setMessage('Unable to verify email. Please check your connection and try again.');
      }
    };

    if (token) {
      verifyEmail();
    } else {
      setStatus('error');
      setMessage('Invalid verification link.');
    }
  }, [token]);

  const handleResendVerification = async () => {
    if (!email.trim()) {
      setResendMessage('Please enter your email address.');
      return;
    }

    setResendStatus('sending');
    setResendMessage('');

    try {
      const response = await fetch(
        `${API_URL}/console/auth/resend-verification`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ email: email.trim() }),
        }
      );

      const data = await response.json();

      if (response.ok) {
        if (data.status === 'already_verified') {
          setResendStatus('sent');
          setResendMessage('This email is already verified. You can sign in now.');
        } else {
          setResendStatus('sent');
          setResendMessage('Verification email sent! Check your inbox.');
        }
      } else {
        setResendStatus('error');
        setResendMessage(data.detail || 'Failed to send verification email.');
      }
    } catch (error) {
      setResendStatus('error');
      setResendMessage('Unable to send email. Please try again.');
    }
  };

  const handleContinue = () => {
    onComplete();
  };

  return (
    <div className="fixed inset-0 bg-gradient-to-br from-teal-50 to-cyan-50 dark:from-gray-900 dark:to-gray-800 flex items-center justify-center p-4 z-50">
      <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl max-w-md w-full p-8 text-center">
        {status === 'loading' && (
          <>
            <div className="w-16 h-16 mx-auto mb-6 bg-teal-100 dark:bg-teal-900/30 rounded-full flex items-center justify-center">
              <Loader2 className="w-8 h-8 text-teal-600 dark:text-teal-400 animate-spin" />
            </div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
              Verifying your email...
            </h1>
            <p className="text-gray-600 dark:text-gray-400">
              Please wait while we confirm your email address.
            </p>
          </>
        )}

        {status === 'success' && (
          <>
            <div className="w-16 h-16 mx-auto mb-6 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center">
              <CheckCircle className="w-8 h-8 text-green-600 dark:text-green-400" />
            </div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
              Email Verified! 🎉
            </h1>
            <p className="text-gray-600 dark:text-gray-400 mb-6">
              {message}
            </p>
            <p className="text-gray-500 dark:text-gray-500 text-sm mb-6">
              You can now sign in and start exploring Coordal's location platform.
            </p>
            <button
              onClick={handleContinue}
              className="w-full py-3 px-4 bg-gradient-to-r from-teal-500 to-cyan-500 text-white font-semibold rounded-xl hover:from-teal-600 hover:to-cyan-600 transition-all"
            >
              Continue to Map
            </button>
          </>
        )}

        {status === 'error' && (
          <>
            <div className="w-16 h-16 mx-auto mb-6 bg-red-100 dark:bg-red-900/30 rounded-full flex items-center justify-center">
              <XCircle className="w-8 h-8 text-red-600 dark:text-red-400" />
            </div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
              Verification Failed
            </h1>
            <p className="text-gray-600 dark:text-gray-400 mb-6">
              {message}
            </p>
            <button
              onClick={handleContinue}
              className="w-full py-3 px-4 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 font-semibold rounded-xl hover:bg-gray-200 dark:hover:bg-gray-600 transition-all"
            >
              Go to Map
            </button>
          </>
        )}

        {status === 'expired' && (
          <>
            <div className="w-16 h-16 mx-auto mb-6 bg-amber-100 dark:bg-amber-900/30 rounded-full flex items-center justify-center">
              <Mail className="w-8 h-8 text-amber-600 dark:text-amber-400" />
            </div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
              Link Expired
            </h1>
            <p className="text-gray-600 dark:text-gray-400 mb-4">
              {message}
            </p>

            {resendStatus !== 'sent' ? (
              <>
                <p className="text-gray-500 dark:text-gray-500 text-sm mb-4">
                  Enter your email and we'll send you a new verification link instantly.
                </p>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Enter your email"
                  className="w-full px-4 py-3 mb-4 border border-gray-300 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-teal-500"
                />
                {resendMessage && (
                  <p className={`text-sm mb-4 ${resendStatus === 'error' ? 'text-red-500' : 'text-gray-500'}`}>
                    {resendMessage}
                  </p>
                )}
                <button
                  onClick={handleResendVerification}
                  disabled={resendStatus === 'sending'}
                  className="w-full py-3 px-4 bg-gradient-to-r from-teal-500 to-cyan-500 text-white font-semibold rounded-xl hover:from-teal-600 hover:to-cyan-600 transition-all disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {resendStatus === 'sending' ? (
                    <>
                      <Loader2 className="w-5 h-5 animate-spin" />
                      Sending...
                    </>
                  ) : (
                    <>
                      <Send className="w-5 h-5" />
                      Resend Verification Email
                    </>
                  )}
                </button>
                <p className="text-xs text-gray-400 mt-3">
                  Takes less than 10 seconds. No need to sign in again.
                </p>
              </>
            ) : (
              <>
                <div className="w-12 h-12 mx-auto mb-4 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center">
                  <CheckCircle className="w-6 h-6 text-green-600 dark:text-green-400" />
                </div>
                <p className="text-green-600 dark:text-green-400 font-medium mb-4">
                  {resendMessage}
                </p>
              </>
            )}

            <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
              <button
                onClick={handleContinue}
                className="text-sm text-gray-500 hover:text-gray-700 dark:hover:text-gray-300"
              >
                Back to Sign In
              </button>
            </div>
          </>
        )}

        <div className="mt-8 pt-6 border-t border-gray-200 dark:border-gray-700">
          <p className="text-xs text-gray-500 dark:text-gray-500">
            Coordal - Location Infrastructure for Africa
          </p>
        </div>
      </div>
    </div>
  );
}
